import { useEffect } from "react";
import { useForm } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { isValid as dateIsValid, format } from "date-fns";

import DateStringFormField from "components/date-string-form-field";
import Dialog from "components/dialog";
import {
  PATIENT_PRODUCT_STATUSES,
  PATIENT_PRODUCT_STATUS_COLORS
} from "constants/patient-products";
import { formatDateOnlyValue } from "utils/common";

export interface RedeliveryDevice {
  readonly id: number;
  readonly name: string | null;
  readonly serial_number: string | null;
  readonly was_delivered_before: boolean;
  readonly statuses: ReadonlyArray<{ value: number; created_at: string }>;
  readonly current_delivered_at: string | null;
  readonly proposed_delivered_at: string | null;
}

interface RedeliveryDateModalProps {
  readonly open: boolean;
  readonly devices: ReadonlyArray<RedeliveryDevice>;
  readonly isSubmitting?: boolean;
  readonly onDeliver: (overrides: Record<string, string | null>) => void;
  readonly onCancel: () => void;
}

type OverrideFormValues = Record<string, string>;

const asDisplayDate = (value: string | null | undefined) =>
  formatDateOnlyValue(value ?? undefined) || "—";

// Newest first, matching the Status History on the device page.
const newestFirst = (statuses: RedeliveryDevice["statuses"]) =>
  [...statuses].reverse();

export default function RedeliveryDateModal({
  open,
  devices,
  isSubmitting = false,
  onDeliver,
  onCancel
}: RedeliveryDateModalProps) {
  // Seeded with the date the device ALREADY has, and left blank when it has none — never
  // pre-filled with today, which would put a date nobody chose in front of the user.
  const seeded = Object.fromEntries(
    devices.map((device) => [
      String(device.id),
      formatDateOnlyValue(device.current_delivered_at ?? undefined) || ""
    ])
  );

  const { control, getValues, reset } = useForm<OverrideFormValues>({
    defaultValues: seeded
  });

  const seededKey = JSON.stringify(seeded);
  useEffect(() => {
    if (open) reset(JSON.parse(seededKey));
  }, [open, seededKey, reset]);

  // What you see in the field is what the device gets. An empty field means no delivery date —
  // whether you cleared it or it was never set — so it is sent as an explicit null rather than
  // being left out and quietly back-filled with today.
  const handleDeliver = () => {
    const values = getValues();
    const overrides = Object.entries(values).reduce<
      Record<string, string | null>
    >((accumulator, [id, value]) => {
      if (!value || value === "Invalid Date") {
        accumulator[id] = null;
      } else if (value !== seeded[id]) {
        accumulator[id] = value;
      }
      return accumulator;
    }, {});
    onDeliver(overrides);
  };

  return (
    <Dialog
      open={open}
      title="Some of these were delivered before"
      subtitle="Set a delivery date for any of them, or continue without changing anything."
      onClose={onCancel}
      maxWidth="md"
    >
      <Stack divider={<Divider />} spacing={3}>
        {devices.map((device) => {
          return (
            <Stack key={device.id} spacing={1.5} paddingTop={2}>
              <Typography variant="subtitle1">
                {device.name || "Device"}
                {device.serial_number ? ` · ${device.serial_number}` : ""}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 230px",
                  columnGap: 2,
                  alignItems: "center"
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  data-testid={`redelivery-current-${device.id}`}
                >
                  Current delivery date:{" "}
                  <strong>{asDisplayDate(device.current_delivered_at)}</strong>
                </Typography>
                <DateStringFormField
                  name={String(device.id)}
                  label="Set Delivery Date"
                  control={control}
                />
              </Box>

              <Timeline
                position="right"
                sx={{
                  margin: 0,
                  padding: 0,
                  [`& .MuiTimelineItem-root:before`]: { display: "none" }
                }}
              >
                {newestFirst(device.statuses).map((status, index, all) => (
                  <TimelineItem key={`${status.value}-${status.created_at}`}>
                    <TimelineSeparator>
                      <TimelineDot
                        sx={{
                          backgroundColor:
                            PATIENT_PRODUCT_STATUS_COLORS[status.value] ||
                            "#e0e0e0",
                          border: "none"
                        }}
                      />
                      {index < all.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={PATIENT_PRODUCT_STATUSES.getFromCode(
                            status.value
                          )}
                          size="small"
                          sx={{
                            backgroundColor:
                              PATIENT_PRODUCT_STATUS_COLORS[status.value] ||
                              "#e0e0e0",
                            color: "white"
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {dateIsValid(new Date(status.created_at))
                            ? format(
                                new Date(status.created_at),
                                "MMM dd, yyyy HH:mm"
                              )
                            : "--"}
                        </Typography>
                      </Stack>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Stack>
          );
        })}
      </Stack>

      <DialogActions sx={{ paddingX: 0, paddingTop: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          data-testid="redelivery-cancel"
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={isSubmitting}
          onClick={handleDeliver}
          data-testid="redelivery-deliver"
        >
          Deliver
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
